export enum RobotCropSystem {
  VeselkaCrop = "veselka",
  CarbonCrop = "carbon",
}

export type RobotCropMode = {
  active: RobotCropSystem; // System the robot is actively running.
  allowViewToggle: boolean; // Dual-view toggle is allowed. Still gate on permissions before showing toggle/banner.
};

const isFeatureFlagEnabled = (
  featureFlags: Record<string, boolean> | undefined,
  key: string
): boolean => featureFlags?.[key] ?? false;

export type RobotCropModeInput = {
  featureFlags: Record<string, boolean> | undefined;
};

/**
 * Resolve which crop system a robot is on and whether dual-view is allowed.
 *
 * All gates come from robot.featureFlags (health-log cache on the summary):
 * - Active = Carbon Crop when carbon_crop_feature && embedding classification.
 * - Toggle when Carbon Crop is on and either embedding is off, or embedding is on
 *   with embeddings_based_classification_toggle_feature also on.
 */
export const getRobotCropMode = ({
  featureFlags,
}: RobotCropModeInput): RobotCropMode => {
  const isCarbonEnabled = isFeatureFlagEnabled(
    featureFlags,
    "carbon_crop_feature"
  );
  const isEmbeddingEnabled = isFeatureFlagEnabled(
    featureFlags,
    "embedding_based_classification_feature"
  );
  const embeddingsToggleEnabled = isFeatureFlagEnabled(
    featureFlags,
    "embeddings_based_classification_toggle_feature"
  );

  if (!isCarbonEnabled) {
    return { active: RobotCropSystem.VeselkaCrop, allowViewToggle: false };
  }

  if (!isEmbeddingEnabled) {
    return { active: RobotCropSystem.VeselkaCrop, allowViewToggle: true };
  }

  return {
    active: RobotCropSystem.CarbonCrop,
    allowViewToggle: embeddingsToggleEnabled,
  };
};
